import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, UserPlus } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@/components/ui/SearchableSelect";
import { DateInput } from "@/components/ui/DateInput";
import { useCostCenters } from "@/hooks/useCostCenters";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/utils/cn";
import {
  DESCRICAO_PAGAMENTO_MAX_LENGTH,
  OBSERVACAO_MAX_LENGTH,
  EMPTY_SOLICITACAO_FORM,
  createSolicitacaoPagamento,
  dtoToFormValues,
  fetchCreditorOptions,
  fetchPaymentCategoryOptions,
  fetchSolicitacaoPagamentoFormOptions,
  updateSolicitacaoPagamento,
  type CreditorOption,
  type FormUserOption,
  type SolicitacaoPagamentoDto,
  type SolicitacaoPagamentoFormValues,
} from "@/services/solicitacaoPagamento";
import { SolicitacaoPagamentoStepper } from "./SolicitacaoPagamentoStepper";
import { SolicitacaoPagamentoReviewModal } from "./SolicitacaoPagamentoReviewModal";
import { CadastrarFornecedorModal } from "./CadastrarFornecedorModal";
import { validateStep, type WizardStep } from "./formUtils";
import { PagamentoMetodoFields } from "./PagamentoMetodoFields";
import { clearDadosPagamentoForForma } from "./pagamentoMetodoUtils";

interface Props {
  onSubmitted: () => void;
  editItem?: SolicitacaoPagamentoDto | null;
  duplicateFrom?: SolicitacaoPagamentoDto | null;
  onCancelEdit?: () => void;
}

export function SolicitacaoPagamentoWizard({ onSubmitted, editItem, duplicateFrom, onCancelEdit }: Props) {
  const isEditing = editItem != null;
  const { user } = useAuth();
  const { options: ccOptions, loading: loadingCc, error: ccError } = useCostCenters();

  const [step, setStep] = useState<WizardStep>(1);
  const [values, setValues] = useState<SolicitacaoPagamentoFormValues>(EMPTY_SOLICITACAO_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [loadingOptions, setLoadingOptions] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [aprovadores, setAprovadores] = useState<FormUserOption[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<Array<{ value: string; label: string }>>([]);

  const [loadingPlanos, setLoadingPlanos] = useState(true);
  const [planoOptions, setPlanoOptions] = useState<SearchableSelectOption[]>([]);

  const [loadingCreditors, setLoadingCreditors] = useState(true);
  const [creditors, setCreditors] = useState<CreditorOption[]>([]);
  const [creditorModalOpen, setCreditorModalOpen] = useState(false);

  const loadCreditors = useCallback(async () => {
    setLoadingCreditors(true);
    try {
      const opts = await fetchCreditorOptions();
      setCreditors(opts);
    } finally {
      setLoadingCreditors(false);
    }
  }, []);

  useEffect(() => {
    if (editItem) {
      setValues(dtoToFormValues(editItem));
      setStep(1);
      setErrors({});
      setSubmitError(null);
      return;
    }
    if (duplicateFrom) {
      setValues({
        ...dtoToFormValues(duplicateFrom),
        solicitanteId: user?.id ?? duplicateFrom.solicitanteId,
        anexoFile: null,
      });
      setStep(1);
      setErrors({});
      setSubmitError(null);
    }
  }, [editItem, duplicateFrom, user?.id]);

  useEffect(() => {
    if (user?.id && !editItem && !duplicateFrom) {
      setValues((prev) => ({ ...prev, solicitanteId: user.id }));
    }
  }, [user?.id, editItem, duplicateFrom]);

  useEffect(() => {
    let cancelled = false;
    setLoadingOptions(true);
    fetchSolicitacaoPagamentoFormOptions()
      .then((opts) => {
        if (cancelled) return;
        const solicitanteId =
          editItem?.solicitanteId ??
          (duplicateFrom ? user?.id : undefined) ??
          opts.solicitante?.id ??
          user?.id ??
          "";
        const eligibleAprovadores = opts.aprovadores.filter((a) => a.id !== solicitanteId);
        setAprovadores(eligibleAprovadores);
        setFormasPagamento(opts.formasPagamento);
        setValues((prev) => {
          const nextSolicitanteId = solicitanteId || prev.solicitanteId;
          const aprovadorId =
            prev.aprovadorId && prev.aprovadorId !== nextSolicitanteId ? prev.aprovadorId : "";
          return { ...prev, solicitanteId: nextSolicitanteId, aprovadorId };
        });
        if (eligibleAprovadores.length === 0) {
          setOptionsError("Nenhum aprovador cadastrado. Peça ao administrador para liberar a permissão de aprovação.");
        }
      })
      .catch(() => {
        if (!cancelled) setOptionsError("Não foi possível carregar os aprovadores.");
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, editItem?.solicitanteId, duplicateFrom?.id]);

  useEffect(() => {
    let cancelled = false;
    setLoadingPlanos(true);
    fetchPaymentCategoryOptions()
      .then((opts) => {
        if (!cancelled) setPlanoOptions(opts);
      })
      .finally(() => {
        if (!cancelled) setLoadingPlanos(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadCreditors().catch(() => {
      if (!cancelled) setCreditors([]);
    });
    return () => {
      cancelled = true;
    };
  }, [loadCreditors]);

  useEffect(() => {
    if (!values.creditorId || values.creditorDocument) return;
    const c = creditors.find((x) => x.id === values.creditorId);
    if (!c?.documentNumber) return;
    setValues((prev) => ({
      ...prev,
      creditorName: c.name || prev.creditorName,
      creditorDocument: c.documentNumber,
    }));
  }, [creditors, values.creditorId, values.creditorDocument]);

  const aprovadorOptions = useMemo(
    () =>
      aprovadores
        .filter((s) => s.id !== values.solicitanteId)
        .map((s) => ({ value: s.id, label: s.displayName })),
    [aprovadores, values.solicitanteId],
  );
  const creditorOptions = useMemo(
    () =>
      creditors.map((c) => ({
        value: c.id,
        label: c.documentNumber ? `${c.name} (${c.documentNumber})` : c.name,
      })),
    [creditors],
  );

  const solicitanteLabel = user?.displayName ?? "";
  const aprovadorLabel =
    aprovadores.find((s) => s.id === values.aprovadorId)?.displayName ?? "";

  const patch = useCallback(
    (partial: Partial<SolicitacaoPagamentoFormValues>) => {
      setValues((prev) => ({ ...prev, ...partial }));
      setErrors({});
    },
    [],
  );

  const goNext = () => {
    const stepErrors = validateStep(step, values);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    if (step < 3) setStep((s) => (s + 1) as WizardStep);
  };

  const goBack = () => {
    setErrors({});
    if (step > 1) setStep((s) => (s - 1) as WizardStep);
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (isEditing && editItem) {
        await updateSolicitacaoPagamento(editItem.id, values);
      } else {
        await createSolicitacaoPagamento(values);
      }
      setReviewOpen(false);
      if (!isEditing) {
        setValues({
          ...EMPTY_SOLICITACAO_FORM,
          solicitanteId: user?.id ?? "",
        });
        setStep(1);
      }
      onSubmitted();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e instanceof Error ? e.message : isEditing ? "Erro ao reenviar solicitação." : "Erro ao enviar solicitação.");
      setSubmitError(msg);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCreditorChange = (creditorId: string) => {
    const c = creditors.find((x) => x.id === creditorId);
    patch({
      creditorId,
      creditorName: c?.name ?? "",
      creditorDocument: c?.documentNumber ?? "",
    });
  };

  const handleCostCenterChange = (costCenterId: string) => {
    const label = ccOptions.find((o) => o.value === costCenterId)?.label ?? costCenterId;
    patch({ costCenterId, costCenterLabel: label });
  };

  const handlePlanoChange = (paymentCategoryId: string) => {
    const option = planoOptions.find((o) => o.value === paymentCategoryId);
    if (!option || option.disabled) return;
    patch({ paymentCategoryId, paymentCategoryLabel: option.label });
  };

  const handleCreditorCreated = (creditor: CreditorOption) => {
    setCreditors((prev) => {
      const exists = prev.some((c) => c.id === creditor.id);
      if (exists) return prev;
      return [...prev, creditor].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    });
    patch({
      creditorId: creditor.id,
      creditorName: creditor.name,
      creditorDocument: creditor.documentNumber,
    });
  };

  return (
    <>
      <Card className="p-5 md:p-6">
        <div className="mb-6 pb-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800">
            {isEditing ? "Editar solicitação reprovada" : "Nova solicitação de pagamento"}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {isEditing
              ? "Corrija os dados e reenvie para aprovação operacional"
              : `Etapa ${step} de 3 — preencha os dados e envie para aprovação`}
          </p>
        </div>

        <SolicitacaoPagamentoStepper currentStep={step} />

        {(optionsError || ccError) && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{optionsError ?? ccError}</span>
          </div>
        )}

        <div className="mt-6 space-y-4">
          {step === 1 && (
            <>
              <Input
                label="Solicitante"
                value={solicitanteLabel}
                readOnly
                disabled
                hint="Preenchido automaticamente com seu usuário logado"
              />
              <Select
                label="Enviar para aprovação *"
                options={aprovadorOptions}
                value={values.aprovadorId}
                onChange={(e) => patch({ aprovadorId: e.target.value })}
                loading={loadingOptions}
                error={errors.aprovadorId}
                hint="Usuários com permissão de aprovação de pagamentos"
              />
            </>
          )}

          {step === 2 && (
            <>
              <SearchableSelect
                label="Centro de custo *"
                options={ccOptions}
                value={values.costCenterId}
                onChange={handleCostCenterChange}
                loading={loadingCc}
                error={errors.costCenterId}
                dropdownMinWidth={360}
              />
              <SearchableSelect
                label="Plano financeiro *"
                options={planoOptions}
                value={values.paymentCategoryId}
                onChange={handlePlanoChange}
                loading={loadingPlanos}
                error={errors.paymentCategoryId}
                dropdownMinWidth={360}
              />
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Fornecedor *
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    leftIcon={<UserPlus className="w-3.5 h-3.5" />}
                    onClick={() => setCreditorModalOpen(true)}
                  >
                    Cadastrar fornecedor
                  </Button>
                </div>
                <SearchableSelect
                  options={creditorOptions}
                  value={values.creditorId}
                  onChange={handleCreditorChange}
                  loading={loadingCreditors}
                  error={errors.creditorId}
                  dropdownMinWidth={440}
                  placeholder="Selecione..."
                />
              </div>
              {values.creditorId && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Nome" value={values.creditorName} readOnly disabled />
                  <Input label="CPF / CNPJ" value={values.creditorDocument} readOnly disabled />
                </div>
              )}
              <Input
                label="Descrição do pagamento *"
                value={values.descricao}
                onChange={(e) =>
                  patch({ descricao: e.target.value.slice(0, DESCRICAO_PAGAMENTO_MAX_LENGTH) })
                }
                maxLength={DESCRICAO_PAGAMENTO_MAX_LENGTH}
                hint={`Máximo ${DESCRICAO_PAGAMENTO_MAX_LENGTH} caracteres`}
                error={errors.descricao}
              />
              <Input
                label="Valor *"
                value={values.valor}
                onChange={(e) => patch({ valor: e.target.value })}
                placeholder="0,00"
                inputMode="decimal"
                error={errors.valor}
              />
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Observação
                </label>
                <textarea
                  value={values.observacao}
                  onChange={(e) =>
                    patch({ observacao: e.target.value.slice(0, OBSERVACAO_MAX_LENGTH) })
                  }
                  rows={3}
                  maxLength={OBSERVACAO_MAX_LENGTH}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600"
                  placeholder="Informações adicionais (opcional)"
                />
                <p className="text-xs text-slate-500">Máximo {OBSERVACAO_MAX_LENGTH} caracteres</p>
                {errors.observacao && (
                  <p className="text-xs text-red-500 font-medium">{errors.observacao}</p>
                )}
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Forma de pagamento *
                </span>
                <div className="flex flex-wrap gap-4">
                  {formasPagamento.map((fp) => (
                    <label
                      key={fp.value}
                      className={cn(
                        "inline-flex items-center gap-2 cursor-pointer rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors",
                        values.formaPagamento === fp.value
                          ? "border-brand-600 bg-brand-50 text-brand-700"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300",
                      )}
                    >
                      <input
                        type="radio"
                        name="formaPagamento"
                        value={fp.value}
                        checked={values.formaPagamento === fp.value}
                        onChange={() =>
                          patch({
                            formaPagamento: fp.value as SolicitacaoPagamentoFormValues["formaPagamento"],
                            ...clearDadosPagamentoForForma(
                              fp.value as SolicitacaoPagamentoFormValues["formaPagamento"],
                            ),
                            anexoFile: null,
                          })
                        }
                        className="text-brand-600 focus:ring-brand-600"
                      />
                      {fp.label}
                    </label>
                  ))}
                </div>
                {errors.formaPagamento && (
                  <p className="text-xs text-red-500 font-medium">{errors.formaPagamento}</p>
                )}
              </div>

              <DateInput
                label="Data de vencimento *"
                value={values.dueDate}
                onChange={(dueDate) => patch({ dueDate })}
                error={errors.dueDate}
              />

              <PagamentoMetodoFields
                formaPagamento={values.formaPagamento}
                values={values}
                errors={errors}
                onPatch={patch}
                existingAnexoName={isEditing ? editItem?.anexoOriginalName : null}
              />
            </>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button variant="secondary" onClick={goBack}>
                Voltar
              </Button>
            )}
            {isEditing && onCancelEdit && (
              <Button variant="ghost" onClick={onCancelEdit}>
                Cancelar edição
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 mr-2">{step}/3</span>
            {step < 3 ? (
              <Button onClick={goNext}>Próximo</Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setReviewOpen(true)}>
                  Revisar
                </Button>
                <Button onClick={() => {
                  const stepErrors = validateStep(3, values);
                  if (Object.keys(stepErrors).length > 0) {
                    setErrors(stepErrors);
                    return;
                  }
                  setReviewOpen(true);
                }}>
                  {isEditing ? "Reenviar para aprovação" : "Enviar solicitação"}
                </Button>
              </>
            )}
          </div>
        </div>

        {submitError && (
          <p className="mt-4 text-sm text-red-600">{submitError}</p>
        )}
      </Card>

      <CadastrarFornecedorModal
        open={creditorModalOpen}
        onClose={() => setCreditorModalOpen(false)}
        onCreated={handleCreditorCreated}
      />

      <SolicitacaoPagamentoReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onConfirm={handleSubmit}
        values={values}
        solicitanteLabel={solicitanteLabel}
        aprovadorLabel={aprovadorLabel}
        loading={submitting}
        existingAnexoName={isEditing ? editItem?.anexoOriginalName : null}
        mode={isEditing ? "edit" : "create"}
      />
    </>
  );
}
